import { useEffect, useRef, type PointerEvent, type RefObject } from 'react';

import AnillosGuia from '@/components/marketing/comun/AnillosGuia';
import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import MarcoEscalado from '@/components/marketing/comun/MarcoEscalado';
import estilosFlota from '@/components/marketing/comun/flota.module.css';
import { CLASE_TONO } from '@/components/marketing/comun/tonosRegistro';
import { CLAVES_BIBLIOTECA, GEOMETRIA_ESCRITORIO, generarRegistros, TRAZADO_ESCRITORIO, type TrazadoBiblioteca } from '@/lib/marketing/geometriaBiblioteca';
import { cn } from '@/lib/utils';

import estilos from './biblioteca.module.css';
import {
    CENTRO_ESCRITORIO,
    desplazamientoRegistro,
    funcionEnPunto,
    puntoEnDiseno,
    SEPARACION_ESCRITORIO,
    transformacionRegistro,
    traslacionEmpuje,
    type Punto,
} from './estadoBiblioteca';
import { contadorRegistros, etiquetaFuncion, TEXTOS_FUNCIONES, TEXTOS_SECCION, type TextoFuncion } from './textosBiblioteca';

// Los registros salen de la geometría compartida con el héroe; se generan una vez por módulo.
export const REGISTROS_ESCRITORIO = generarRegistros(GEOMETRIA_ESCRITORIO);

const ANCHO_ESCENARIO = 1240;

type Lado = 'izquierda' | 'derecha';
type Alineacion = 'centro' | 'abajo' | 'arriba';

export interface Flotacion {
    readonly flota: 1 | 2 | 3;
    readonly duracion: number;
    readonly retraso: number;
}

// Posición absoluta de cada nodo en el escenario: los de la izquierda se anclan por la derecha
// y abren la etiqueta hacia fuera (row-reverse), como en el tablero.
const NODOS: readonly ({ lado: Lado; borde: number; arriba: number; alineacion: Alineacion } & Flotacion)[] = [
    { lado: 'derecha', borde: 833, arriba: 161, alineacion: 'centro', flota: 2, duracion: 1.8, retraso: -0.2 },
    { lado: 'derecha', borde: 695, arriba: 23, alineacion: 'abajo', flota: 3, duracion: 2.2, retraso: -1.3 },
    { lado: 'izquierda', borde: 695, arriba: 23, alineacion: 'abajo', flota: 1, duracion: 1.6, retraso: -0.7 },
    { lado: 'izquierda', borde: 833, arriba: 161, alineacion: 'centro', flota: 3, duracion: 2.4, retraso: -1.9 },
    { lado: 'izquierda', borde: 833, arriba: 355, alineacion: 'centro', flota: 2, duracion: 1.95, retraso: -0.5 },
    { lado: 'izquierda', borde: 695, arriba: 493, alineacion: 'arriba', flota: 3, duracion: 2.1, retraso: -1.6 },
    { lado: 'derecha', borde: 695, arriba: 493, alineacion: 'arriba', flota: 1, duracion: 1.7, retraso: -1 },
    { lado: 'derecha', borde: 833, arriba: 355, alineacion: 'centro', flota: 2, duracion: 2.3, retraso: -2.1 },
];

const CLASE_ALINEACION: Record<Alineacion, string> = { centro: 'items-center', abajo: 'items-end', arriba: 'items-start' };
const MARGEN_ETIQUETA: Record<Alineacion, string> = { centro: '', abajo: 'mb-[5px]', arriba: 'mt-[5px]' };
const CLASE_FLOTA: Record<Flotacion['flota'], string> = { 1: estilosFlota.flota1, 2: estilosFlota.flota2, 3: estilosFlota.flota3 };

export type EstadoNodo = 'reposo' | 'activa' | 'atenuada';

export function estadoNodo(indice: number, activa: number): EstadoNodo {
    if (activa < 0) return 'reposo';
    return indice === activa ? 'activa' : 'atenuada';
}

export const CLASE_SECTOR: Record<EstadoNodo, string> = {
    reposo: 'fill-surface-1 stroke-surface-1',
    activa: 'fill-tint-accent-sector stroke-tint-accent-sector',
    atenuada: 'fill-surface-suave stroke-surface-suave',
};

export const CLASE_ARCO: Record<EstadoNodo, string> = {
    reposo: 'stroke-ink',
    activa: 'stroke-brand-600',
    atenuada: 'stroke-ink/25',
};

const CLASE_ICONO: Record<EstadoNodo, string> = {
    reposo: 'bg-surface-2 border-ink text-primary shadow-ds-chip',
    activa: 'bg-accent-blue border-ink text-ink shadow-ds-halo-activo',
    atenuada: 'bg-surface-2 border-ink/25 text-ink/55 shadow-ds-chip-atenuada',
};

const CLASE_ETIQUETA: Record<EstadoNodo, string> = {
    reposo: 'border-transparent whitespace-nowrap text-ink',
    activa: 'border-ink shadow-ds-chip whitespace-normal text-ink',
    atenuada: 'border-transparent whitespace-nowrap text-on-surface-variant',
};

// Transiciones del tablero (.bibsec, .biblomo, .bibico); con movimiento reducido solo color y opacidad.
export const TRANSICION_SECTOR =
    'transition-[fill,stroke,transform] [transition-duration:350ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-[fill,stroke] motion-reduce:[transition-duration:200ms]';
export const TRANSICION_REGISTRO =
    'transition-[transform,translate,opacity] [transition-duration:450ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-opacity motion-reduce:[transition-duration:200ms]';
export const TRANSICION_ICONO = 'transition-[background-color,border-color,color,box-shadow] duration-200';

// La clase y el estilo de la flotación de un nodo; nada flota con movimiento reducido.
export function flotacionNodo(flotacion: Flotacion, activo: boolean, reducido: boolean) {
    if (reducido) return { className: '', style: {} };
    return {
        className: cn(CLASE_FLOTA[flotacion.flota], activo && estilosFlota.pausada),
        style: { animationDuration: `${flotacion.duracion}s`, animationDelay: `${flotacion.retraso}s` },
    };
}

export function IconoFuncion({ texto }: { texto: TextoFuncion }) {
    return (
        <svg
            width="19"
            height="19"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap={texto.extremosRectos ? undefined : 'round'}
            strokeLinejoin={texto.extremosRectos ? undefined : 'round'}
            aria-hidden="true"
        >
            {texto.figuras.map((figura, i) => {
                if (figura.tipo === 'rect') return <rect key={i} x={figura.x} y={figura.y} width={figura.ancho} height={figura.alto} rx={figura.radio} />;
                if (figura.tipo === 'circulo') return <circle key={i} cx={figura.cx} cy={figura.cy} r={figura.r} />;
                return <path key={i} d={figura.d} />;
            })}
        </svg>
    );
}

interface PropiedadesSectores {
    trazado: TrazadoBiblioteca;
    activa: number;
    reducido: boolean;
}

/** Sectores, arcos y anillos de los dos árboles: los trazados compartidos con el héroe (geometriaBiblioteca.ts). */
export function SectoresBiblioteca({ trazado, activa, reducido }: PropiedadesSectores) {
    const crece = (i: number) => i === activa && !reducido && 'scale-[1.03]';
    const marca = (i: number) => (i === activa ? '' : undefined);
    const origen = { transformOrigin: `${trazado.centro.x}px ${trazado.centro.y}px` };
    return (
        <>
            <g strokeWidth={trazado.grosorSector} strokeLinejoin="round">
                {trazado.sectores.map((d, i) => (
                    <path key={d} d={d} data-sector={i} data-activa={marca(i)} className={cn(TRANSICION_SECTOR, CLASE_SECTOR[estadoNodo(i, activa)], crece(i))} style={origen} />
                ))}
            </g>
            <g fill="none" strokeWidth="2" strokeLinecap="round">
                {trazado.arcos.map((d, i) => (
                    <path key={d} d={d} className={cn(TRANSICION_SECTOR, CLASE_ARCO[estadoNodo(i, activa)], crece(i))} style={origen} />
                ))}
            </g>
            <AnillosGuia trazado={trazado} />
        </>
    );
}

interface PropiedadesRegistros {
    activa: number;
    reducido: boolean;
    referencias: RefObject<(HTMLSpanElement | null)[]>;
}

// React pinta la separación (`transform`); la repulsión la escribe el fotograma en `translate`.
function Registros({ activa, reducido, referencias }: PropiedadesRegistros) {
    return REGISTROS_ESCRITORIO.map((registro, i) => {
        const esActiva = registro.sector === activa;
        const { mas } = desplazamientoRegistro({ registro, activa: esActiva, separacion: SEPARACION_ESCRITORIO, puntero: null, reducido });
        return (
            <span
                key={i}
                ref={(nodo) => {
                    referencias.current[i] = nodo;
                }}
                data-registro
                className={cn('pointer-events-none absolute left-0 top-0 box-border rounded-[2px] border-[1.5px] border-ink', TRANSICION_REGISTRO, CLASE_TONO[registro.tono])}
                style={{
                    width: registro.largo,
                    height: registro.grosor,
                    opacity: activa >= 0 && !esActiva ? 0.3 : 1,
                    transformOrigin: '0 0',
                    transform: transformacionRegistro(registro, CENTRO_ESCRITORIO, mas),
                }}
            />
        );
    });
}

interface PropiedadesNodo {
    indice: number;
    activa: number;
    pulsada: boolean;
    reducido: boolean;
}

function NodoFuncion({ indice, activa, pulsada, reducido }: PropiedadesNodo) {
    const texto = TEXTOS_FUNCIONES[CLAVES_BIBLIOTECA[indice]];
    const nodo = NODOS[indice];
    const estado = estadoNodo(indice, activa);
    const flotacion = flotacionNodo(nodo, estado === 'activa', reducido);
    const posicion = nodo.lado === 'derecha' ? { right: nodo.borde, top: nodo.arriba } : { left: nodo.borde, top: nodo.arriba };
    return (
        <button
            type="button"
            data-fn={indice}
            data-activa={estado === 'activa' ? '' : undefined}
            aria-pressed={pulsada}
            aria-label={etiquetaFuncion(texto)}
            className={cn(
                'absolute m-0 flex cursor-pointer gap-1 rounded-[26px] border-0 bg-transparent p-0 text-left focus-visible:outline-offset-4',
                nodo.lado === 'derecha' && 'flex-row-reverse',
                CLASE_ALINEACION[nodo.alineacion],
                estado === 'activa' ? 'z-[5]' : 'z-[1]',
                flotacion.className,
            )}
            style={{ ...posicion, ...flotacion.style }}
        >
            <span className={cn('box-border flex size-11 shrink-0 items-center justify-center rounded-full border-2', TRANSICION_ICONO, CLASE_ICONO[estado])}>
                <IconoFuncion texto={texto} />
            </span>
            <span
                className={cn(
                    'box-border max-w-[284px] rounded-ds-md border-2 bg-surface-2 px-2.5 py-[5px] text-[14px] leading-[1.4] transition-[border-color,color] duration-200',
                    MARGEN_ETIQUETA[nodo.alineacion],
                    CLASE_ETIQUETA[estado],
                )}
            >
                <strong className={estado === 'activa' ? 'font-bold' : 'font-semibold'}>{texto.nombre}</strong>
                {estado === 'activa' && <span className={cn('text-ink/90', estilos.entra)}> · {texto.descripcion}</span>}
            </span>
        </button>
    );
}

// La función bajo el puntero: la del botón si señala uno; si no, el sector de la corona sensible.
function funcionBajoPuntero(objetivo: EventTarget, punto: Punto): number {
    const boton = objetivo instanceof Element ? objetivo.closest('[data-fn]') : null;
    if (boton) return Number(boton.getAttribute('data-fn'));
    return funcionEnPunto(GEOMETRIA_ESCRITORIO, punto.x - CENTRO_ESCRITORIO.x, punto.y - CENTRO_ESCRITORIO.y);
}

function escribirEmpujes(nodos: (HTMLSpanElement | null)[], activa: number, puntero: Punto | null) {
    REGISTROS_ESCRITORIO.forEach((registro, i) => {
        const nodo = nodos[i];
        if (!nodo) return;
        const relativo = puntero && { x: puntero.x - CENTRO_ESCRITORIO.x, y: puntero.y - CENTRO_ESCRITORIO.y };
        const desplazamiento = desplazamientoRegistro({ registro, activa: registro.sector === activa, separacion: SEPARACION_ESCRITORIO, puntero: relativo, reducido: false });
        nodo.style.translate = traslacionEmpuje(desplazamiento);
    });
}

interface OpcionesPuntero {
    reducido: boolean;
    // La función activa sin contar el hover: decide qué estante está separado al calcular el empuje.
    activaSinSobre: number;
    alCambiarSobre: (indice: number) => void;
}

/**
 * Hover y repulsión del puntero (UI-SPEC §Interaction 2 «Pointer repel»): como mover() del
 * tablero, un solo requestAnimationFrame por tanda de movimientos. El empuje se escribe en los
 * nodos por referencia dentro de ese fotograma, así los 258 registros no se vuelven a pintar
 * con cada movimiento; el cambio de función activa sí pasa por React.
 */
function usePunteroEscritorio({ reducido, activaSinSobre, alCambiarSobre }: OpcionesPuntero) {
    const registros = useRef<(HTMLSpanElement | null)[]>([]);
    const pendiente = useRef(0);
    const ultimo = useRef<{ punto: Punto; sobre: number } | null>(null);
    const activaBase = useRef(activaSinSobre);
    useEffect(() => {
        activaBase.current = activaSinSobre;
    });
    useEffect(() => () => cancelAnimationFrame(pendiente.current), []);

    function aplicarFotograma() {
        pendiente.current = 0;
        const senal = ultimo.current;
        if (!senal) return;
        alCambiarSobre(senal.sobre);
        escribirEmpujes(registros.current, senal.sobre >= 0 ? senal.sobre : activaBase.current, senal.punto);
    }

    function alMover(evento: PointerEvent<HTMLDivElement>) {
        if (evento.pointerType === 'touch') return;
        const punto = puntoEnDiseno(evento.clientX, evento.clientY, evento.currentTarget.getBoundingClientRect(), ANCHO_ESCENARIO);
        const sobre = funcionBajoPuntero(evento.target, punto);
        if (reducido) return alCambiarSobre(sobre);
        ultimo.current = { punto, sobre };
        if (!pendiente.current) pendiente.current = requestAnimationFrame(aplicarFotograma);
    }

    function alSalir() {
        ultimo.current = null;
        cancelAnimationFrame(pendiente.current);
        pendiente.current = 0;
        alCambiarSobre(-1);
        escribirEmpujes(registros.current, -1, null);
    }

    return { registros, alMover, alSalir };
}

interface PropiedadesEscenario extends OpcionesPuntero {
    activa: number;
    pulsada: number;
    idBase: string;
}

/**
 * El árbol de escritorio de la Biblioteca (Biblioteca.dc.html). La tarjeta ocupa el ancho del
 * contenedor (hasta 1312) y dentro el contenido de 1308×690 se escala entero, así el borde y la
 * sombra de la tarjeta no se recortan. El estado vive en Biblioteca; aquí solo el puntero.
 */
export default function EscenarioBibliotecaEscritorio({ activa, pulsada, idBase, reducido, activaSinSobre, alCambiarSobre }: PropiedadesEscenario) {
    const { registros, alMover, alSalir } = usePunteroEscritorio({ reducido, activaSinSobre, alCambiarSobre });
    return (
        <div data-arbol="escritorio" data-nosnippet className="mt-10 hidden w-full max-w-[1312px] lg:block">
            <div className="box-border overflow-hidden rounded-xl border-2 border-ink bg-surface-2 shadow-ds-2">
                <MarcoEscalado anchoDiseno={1308} altoDiseno={690} escalaInicial={1}>
                    <div className="relative h-[690px] w-[1308px]">
                        <div className="absolute left-8 top-6 flex items-center gap-3.5">
                            <strong className="font-display text-[18px] font-bold leading-[1.2] tracking-[-0.01em]">{TEXTOS_SECCION.tituloTarjeta}</strong>
                            <span className="box-border flex h-[30px] items-center gap-2 whitespace-nowrap rounded-full border-2 border-ink bg-tint-accent px-3 text-[13px] font-bold tabular-nums text-ink">
                                <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-primary" />
                                {contadorRegistros(REGISTROS_ESCRITORIO.length)}
                            </span>
                        </div>
                        <div id={`${idBase}-escenario`} data-bib onPointerMove={alMover} onPointerLeave={alSalir} className="absolute left-8 top-[86px] h-[580px] w-[1240px]">
                            <svg aria-hidden="true" width="1240" height="580" className="absolute left-0 top-0 overflow-visible">
                                <SectoresBiblioteca trazado={TRAZADO_ESCRITORIO} activa={activa} reducido={reducido} />
                            </svg>
                            <Registros activa={activa} reducido={reducido} referencias={registros} />
                            <span
                                role="img"
                                aria-label={TEXTOS_SECCION.paciente}
                                className="absolute left-[572px] top-[232px] flex size-24 items-center justify-center rounded-full bg-ink font-display text-[30px] font-bold tracking-[-0.01em] text-canvas shadow-ds-halo-paciente"
                            >
                                {TEXTOS_SECCION.inicialesPaciente}
                            </span>
                            {CLAVES_BIBLIOTECA.map((clave, i) => (
                                <NodoFuncion key={clave} indice={i} activa={activa} pulsada={pulsada === i} reducido={reducido} />
                            ))}
                            <div className="absolute bottom-0 left-0">
                                <LeyendaIlustracion />
                            </div>
                        </div>
                    </div>
                </MarcoEscalado>
            </div>
        </div>
    );
}
